import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, Mail, MessageCircle, Phone, Truck, Wrench } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { EMAIL, PHONE, deliveryText, gbp, http, imgUrl, typeLabel, waLink, years } from "../lib/api";
import { useCart } from "../lib/cart";
import { EnquiryForm } from "../components/site/EnquiryForm";

const Spec = ({ k, v }) => (
  <div className="flex justify-between gap-4 border-b border-white/10 py-3 text-sm">
    <span className="font-mono text-xs uppercase tracking-widest text-slate-400">{k}</span>
    <span className="text-right text-slate-100">{v || "—"}</span>
  </div>
);

export default function ProductPage() {
  const { id } = useParams();
  const { add } = useCart();
  const [p, setP] = useState(null);
  const [err, setErr] = useState(false);
  const [active, setActive] = useState(0);
  const [ask, setAsk] = useState(false);

  useEffect(() => {
    setP(null);
    setActive(0);
    http.get(`/products/${id}`).then((r) => setP(r.data)).catch(() => setErr(true));
  }, [id]);

  if (err) return <main className="px-4 pb-24 pt-40 text-center"><p className="text-slate-300">This part could not be found.</p><Link to="/shop" className="mt-4 inline-block text-[#F43F5E]">Back to shop</Link></main>;
  if (!p) return <main data-testid="product-loading" className="min-h-screen pt-40" />;

  const imgs = p.images?.length ? p.images : [null];
  const inStock = (p.stock ?? 0) > 0;
  const waMsg = `Hi CBLS AIRBAGS, I'm interested in: ${p.title} (Part ${p.part_number || "n/a"}). Is it available?`;

  return (
    <main data-testid="product-page" className="mx-auto max-w-7xl px-4 pb-24 pt-28 sm:px-8 lg:px-12">
      <Link to="/shop" data-testid="product-back-link" className="inline-flex items-center gap-2 font-mono text-xs uppercase tracking-widest text-slate-300 hover:text-white"><ArrowLeft className="h-4 w-4" /> All parts</Link>
      <div className="mt-8 grid gap-12 lg:grid-cols-12">
        <div className="lg:col-span-7">
          <motion.div initial={{ clipPath: "inset(0 0 100% 0)" }} animate={{ clipPath: "inset(0 0 0% 0)" }} transition={{ duration: 1, ease: [0.16, 1, 0.3, 1] }} className="clip-frame-lg relative aspect-square overflow-hidden bg-[#121820]">
            <img data-testid="product-main-image" src={imgUrl(imgs[active])} alt={p.title} className="h-full w-full object-cover" />
            <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_50%_40%,transparent_35%,rgba(9,13,18,0.7)_100%)]" />
          </motion.div>
          {imgs.length > 1 && (
            <div className="mt-3 grid grid-cols-5 gap-3">
              {imgs.map((im, i) => (
                <button key={i} data-testid={`product-thumb-${i}`} onClick={() => setActive(i)} className={`aspect-square overflow-hidden border-2 transition-colors ${i === active ? "border-[#E11D48]" : "border-transparent opacity-60 hover:opacity-100"}`}>
                  <img src={imgUrl(im)} alt="" className="h-full w-full object-cover" />
                </button>
              ))}
            </div>
          )}
        </div>
        <div className="lg:col-span-5">
          <p className="font-mono text-xs uppercase tracking-[0.25em] text-[#F43F5E]">{typeLabel(p.airbag_type)} · {p.condition}</p>
          <h1 data-testid="product-title" className="mt-4 font-display text-5xl font-black uppercase leading-[0.9] tracking-tight sm:text-6xl">{p.title}</h1>
          <p data-testid="product-price" className="mt-6 font-display text-5xl font-bold">{gbp(p.price)}</p>
          <p data-testid="product-stock" className={`mt-2 font-mono text-xs uppercase tracking-widest ${inStock ? "text-emerald-400" : "text-slate-400"}`}>{inStock ? `${p.stock} in stock` : "Sold out — ask us about more"}</p>
          <p data-testid="product-delivery" className="mt-4 flex gap-2 text-sm text-slate-300"><Truck className="mt-0.5 h-4 w-4 shrink-0 text-[#F59E0B]" /> {deliveryText(p.airbag_type)}</p>
          <div className="mt-8 flex flex-col gap-3">
            <button data-testid="product-add-button" disabled={!inStock} onClick={() => add(p)} className="h-14 rounded-full bg-[#E11D48] font-semibold transition-colors hover:bg-[#F43F5E] disabled:bg-slate-700">Add to basket</button>
            <div className="grid grid-cols-2 gap-3">
              <a data-testid="product-whatsapp-button" href={waLink(waMsg)} target="_blank" rel="noreferrer" className="flex h-12 items-center justify-center gap-2 rounded-full border border-white/15 text-sm font-semibold hover:border-emerald-400 hover:text-emerald-400"><MessageCircle className="h-4 w-4" /> WhatsApp</a>
              <button data-testid="product-enquire-button" onClick={() => setAsk(true)} className="flex h-12 items-center justify-center gap-2 rounded-full border border-white/15 text-sm font-semibold hover:border-white">Check fitment</button>
            </div>
            <a data-testid="product-fitting-button" href={waLink(`Hi CBLS AIRBAGS, I'd like to ask about fitting for: ${p.title} (Part ${p.part_number || "n/a"}). My car: `)} target="_blank" rel="noreferrer" className="flex h-12 items-center justify-center gap-2 rounded-full border border-[#F59E0B]/50 text-sm font-semibold text-[#F59E0B] hover:border-[#F59E0B]">
              <Wrench className="h-4 w-4" /> Ask about fitting
            </a>
            <div className="flex flex-wrap gap-x-6 gap-y-2 pt-2 font-mono text-xs text-slate-300">
              <a href={`tel:${PHONE}`} className="flex items-center gap-2 hover:text-white"><Phone className="h-3 w-3" /> {PHONE}</a>
              <a href={`mailto:${EMAIL}?subject=${encodeURIComponent(p.title)}`} className="flex items-center gap-2 break-all hover:text-white"><Mail className="h-3 w-3" /> {EMAIL}</a>
            </div>
          </div>
          <div className="mt-10">
            <p className="mb-6 text-xs leading-relaxed text-slate-400">14-day returns. Items must be returned in the same condition. Tampering with an airbag will void the warranty.</p>
            <Spec k="Make" v={p.make} />
            <Spec k="Model" v={p.model} />
            <Spec k="Years" v={years(p)} />
            <Spec k="Part number" v={p.part_number} />
            <Spec k="Condition" v={p.condition} />
          </div>
          {p.description && <p data-testid="product-description" className="mt-8 whitespace-pre-line text-base leading-relaxed text-slate-300">{p.description}</p>}
        </div>
      </div>
      <Dialog open={ask} onOpenChange={setAsk}>
        <DialogContent className="border-white/10 bg-[#0D1218] sm:max-w-lg">
          <DialogHeader><DialogTitle className="font-display text-3xl font-extrabold uppercase">Check fitment</DialogTitle></DialogHeader>
          <EnquiryForm product={p} onDone={() => setAsk(false)} />
        </DialogContent>
      </Dialog>
    </main>
  );
}
